import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatAmountWithCommas } from "@unified-checkout/design-system";
import type { IFiltersParams } from "@app/dashboard/types/innerLayout/home";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { getMetricCardsData } from "@app/dashboard/services/inner_layout/home/metric_cards";
import { formatToISODate } from "@app/dashboard/utils/dateFormats";
import { getLang } from "@app/dashboard/utils/localStorageUtils";
import { Metrics } from "@app/dashboard/components/inner_layout/Metrics";

export const MetricCards = () => {

  const queryClient = useQueryClient();

  const lang = getLang();
  const { filters } = useFiltersStore();
  const { t } = useTranslation();

  const { startDate, endDate, compareStartDate, compareEndDate, selectedRangeName, channel, currency, is_live } = filters;

  const metricCardsParams: IFiltersParams = {
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    compare_start_date: formatToISODate(compareStartDate),
    compare_end_date: formatToISODate(compareEndDate),
    channel: channel,
    currency: currency,
    is_live: is_live
  }

  const {
    isLoading,
    isError,
    data: response
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_HOME_METRIC_CARDS_DATA, filters],
    queryFn: () => getMetricCardsData(metricCardsParams),
    enabled: Object.values(filters).every(value => value !== undefined)
  });

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_HOME_METRIC_CARDS_DATA] });
  }, []);

  const metricCardsData = response?.data &&
    Object.values(response?.data).map((item: any) => {
      const { en_title, ar_title, en_currency, ar_currency, info_en, info_ar, value, comparison } = item;
      const ar = lang === 'ar';
      return (
        {
          ...(
            (en_currency && ar_currency) ?
              { currency: ar ? ar_currency : en_currency }
              :
              {}
          ),
          title: ar ? ar_title : en_title,
          info: ar ? info_ar : info_en,
          value: (en_currency && ar_currency) ? formatAmountWithCommas({ amount: value, keepDecimals: true, currency: en_currency, excludeZeroDecimals: true }) : value,
          comparison
        }
      )
    });

  return (
    <Metrics
      data={metricCardsData}
      isLoading={isLoading}
      isError={isError}
      comparisonRangeName={t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.${selectedRangeName}`)}
      noOfCards={3}
    />
  )
}